import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { TalentKategori, Talent } from '../../services/api';

const INGEN_PARENT = '__none__';

interface TalentDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: any) => Promise<void>;
  type: 'kategori' | 'talent';
  mode: 'create' | 'edit';
  item: TalentKategori | Talent | null;
  kategorier: TalentKategori[];
  kategoriSomKanHaTalenter: TalentKategori[];
  initialParentId?: number;
  initialKategoriId?: number;
}

const TalentDialog: React.FC<TalentDialogProps> = ({
  open,
  onClose,
  onSave,
  type,
  mode,
  item,
  kategorier,
  kategoriSomKanHaTalenter,
  initialParentId,
  initialKategoriId,
}) => {
  const [formNavn, setFormNavn] = useState('');
  const [formParentId, setFormParentId] = useState<number | ''>('');
  const [formKategoriId, setFormKategoriId] = useState<number | ''>('');
  const [formBeskrivelse, setFormBeskrivelse] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      if (item) {
        setFormNavn(item.navn);
        setFormBeskrivelse(item.beskrivelse || '');
        if (type === 'kategori') {
          setFormParentId((item as TalentKategori).parent_id || '');
        } else {
          setFormKategoriId((item as Talent).kategori_id);
        }
      } else {
        setFormNavn('');
        setFormBeskrivelse('');
        setFormParentId(initialParentId || '');
        setFormKategoriId(initialKategoriId || '');
      }
    }
  }, [open, item, type, initialParentId, initialKategoriId]);

  const handleSave = async () => {
    try {
      setSaving(true);
      const data: any = {
        navn: formNavn,
        beskrivelse: formBeskrivelse || undefined,
      };

      if (type === 'kategori') {
        data.parentId = formParentId || undefined;
      } else {
        data.kategoriId = formKategoriId;
      }

      await onSave(data);
      onClose();
    } catch (err) {
      console.error('Feil ved lagring:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {mode === 'create' ? 'Opprett' : 'Rediger'} {type === 'kategori' ? 'Kategori' : 'Talent'}
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <div className="space-y-1.5">
            <Label>Navn *</Label>
            <Input
              value={formNavn}
              onChange={(e) => setFormNavn(e.target.value)}
              autoFocus
              required
            />
          </div>

          {type === 'kategori' && (
            <div className="space-y-1.5">
              <Label>Overordnet kategori</Label>
              <Select
                value={formParentId ? String(formParentId) : INGEN_PARENT}
                onValueChange={(v) => setFormParentId(v === INGEN_PARENT ? '' : parseInt(v, 10))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={INGEN_PARENT}>Ingen (root-kategori)</SelectItem>
                  {kategorier.map((k) => (
                    <SelectItem key={k.id} value={String(k.id)}>
                      {k.path || k.navn}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {type === 'talent' && (
            <div className="space-y-1.5">
              <Label>Kategori *</Label>
              <Select
                value={formKategoriId ? String(formKategoriId) : ''}
                onValueChange={(v) => setFormKategoriId(parseInt(v, 10))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {kategoriSomKanHaTalenter.map((k) => (
                    <SelectItem key={k.id} value={String(k.id)}>
                      {k.path || k.navn}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Velg en kategori som ikke har sub-kategorier
              </p>
            </div>
          )}

          <div className="space-y-1.5">
            <Label>Beskrivelse</Label>
            <Textarea
              value={formBeskrivelse}
              onChange={(e) => setFormBeskrivelse(e.target.value)}
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Avbryt
          </Button>
          <Button
            onClick={handleSave}
            disabled={!formNavn || (type === 'talent' && !formKategoriId) || saving}
          >
            {saving ? 'Lagrer...' : 'Lagre'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TalentDialog;
